/*
 * A wrapper containing the wallet balance, available balance, and float widgets. when the account is wallet based.
 * This is intended for use in the reseller dashboard, and in the reseller budget tab
 * This contains an optional float limit increase request.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import AbEnvironment from '../auth/AbEnvironment'; 
import withStyles from '@mui/styles/withStyles';
import { Button, Grid, TextField, Tooltip, Typography } from '@mui/material';
import AvailableBalanceTextWidget from './AvailableBalanceTextWidget';
import TextWidget from '../ui/TextWidget';
import LinearProgressWidget from '../ui/LinearProgressWidget';

import CreditCard from '@mui/icons-material/CreditCard';
import LimitRequestDialog from './LimitRequestDialog';
import CurrencyTextField from '../ui/CurrencyTextField';
import InitiateTransferDialog from './InitiateTransferDialog';

import AppBindStyles from '../../AppBindStyles';
import LoadingSpinner from '../ui/LoadingSpinner';
import { getCurrencyString } from '../../utils/InputUtils';

const abEnvironment = new AbEnvironment();
const WalletBalanceWidgetsQuery = graphql`
query WalletBalanceWidgetsQuery($walletId: UUID!, $accountId: UUID!) {

  getwalletledgersummary(walletid: $walletId, balancedate: null) {
    assetBalance
    assetAndFullArBalance
    assetAndArBalance
    assetArAuthBalance
    authBalance
    floatBalanceForAuth
  }
  getunreceivedchargedetail(accountid: $accountId) { 
    totalCount nodes { 
      transactionId transactionType transactionTime transDescription extSource extReferral extTransactionId changeType changeAmount 
    } 
  }
  accountTByAccountId(accountId: $accountId) {
    achPercAvailableOnCharge 
    achMinAvailableOnCharge
    ccPercAvailableOnCharge
    ccMinAvailableOnCharge
  }
  allWalletTs(condition: {accountId: $accountId}) {
    totalCount
    nodes {
      walletId
    }
  }
}
`;

const styleSheet = theme => ({
  root: {
    width: '100%',
  },
  buttonicon: {
    marginRight: 6,
  },
  instructButton: {
    marginTop: -1,
  },
  widgetContainer: {
    // marginTop: 8,
    //marginBottom: 8,
  }
});
  
/*
 * Props coming in:
 * - narrow           (use narrower tiles)
 * - auth
 * - showEditLink     (show text describing settings, and a link to the budget tab to change the settings)
 * - payFromBalance
 * - autoTopupAmount    (Number)
 * - autoTopupThreshold (Number)
 * - showLimitRequest (show the button / dialog for requesting an increase)
 * - floatLimit       (limit on the account)
 * - accountId        (for lookup)
 * - history
 */
class WalletBalanceWidgets extends Component {
    
  constructor(props) {
    super(props);

    this.state = {
      currentFloatLimit: 0,
      floatLimit: 0,
      showLimitRequestDialog: false,
      limitRequestReason: '',
      transferAmount: 0,
      showTransferDialog: false,
      existingPaymentRailsAccount: false,
      showPayoutDialog: false,
      payoutAmount: 0,
      executingTransfer: false,
      forceFetch: 0
    };  
  }

  componentWillUnmount() {
    this.mounted = false; // Antipattern workaround
  }

  showLimitRequestDialog = (floatLimit) => {
    let setFloatLimit = this.props.floatLimit;
    if (this.state.floatLimit > 0) {
      setFloatLimit = this.state.floatLimit;
    }
    this.setState({
      showLimitRequestDialog: true,
      currentFloatLimit: floatLimit,
      floatLimit: setFloatLimit
    });
  }

  sendLimitIncreaseRequest(accountId) {
    // For now, notification
    const { callPartnerApi } = this.props.auth;
    if (abEnvironment.isSegmentActive()) { window.analytics.track('Limit Increase Request'); }

    let theBody = 'You requested';

    if (this.state.floatLimit !== this.state.currentFloatLimit) {
      let reqFloat = Number(this.state.floatLimit).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
      theBody = theBody + ' an outstanding balance limit of ' + reqFloat;

      let aPjwt = abEnvironment.getPjwt();
      theBody = theBody + ' with this reason: ' + this.state.limitRequestReason;

      let payload = {
        operation: "createNotification",
        params: {
          pJwt:             aPjwt,
          accountId:        accountId,
          notificationType: 'increase',
          severity:         'info',
          subject:          'Limit increase request',
          body:             theBody
        }
      }
      
      callPartnerApi('appbind/generic',
                    'POST',
                    payload
      )
      .then(response => {
        this.setState({showLimitRequestDialog: false, limitRequestReason: ''});
      })
      .catch(error => {
        this.setState({showLimitRequestDialog: false, limitRequestReason: ''});
      });
    }
  }

  goTo(route, theTab) {
    if (theTab != null) {
      localStorage.setItem('psInOut', theTab);
    }
    this.props.history.push(`/${route}`);
  }

  showTransferDialog = () => {
    
    //console.log('showing existing payment rails account');
    this.setState({ showTransferDialog: true });
  }

  initiateTransfer(accountId, walletId) {
    const { callPartnerApi } = this.props.auth;

    let stringAmt = Number(this.state.transferAmount).toString()

    this.setState({executingTransfer: true});

    let aPjwt = abEnvironment.getPjwt();

    let payload = {
      operation: "transferAccountAssetWalletAsset",
      params: {
        pJwt: aPjwt,
        accountId: accountId,
        walletId: walletId,
        transferAmount: stringAmt,
        direction: 1
      }
    };

    callPartnerApi('appbind/generic',
                   'POST',
                   payload
    )
    .then(resp => {
      return resp.json();
    })
    .then(response => {
      let abpResponse = response.data;
      if (abpResponse.result.statusCode === 0) {
        this.setState({ saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Transfer successful', saveSnackbarDuration: null });
        window.location.reload();
      } else {
        this.setState({ saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'There was a problem with transfer.  Please contact AppBind support.', saveSnackbarDuration: null });
      }
    })
 
    .catch(error => {
      let theErrorText = "Unidentified Error";

      let abpResponse = error.data;
      if (abpResponse != null && abpResponse.message != null) {
        theErrorText = abpResponse.message;
      }
      this.setState({ saveSnackbarOpen: true, saveStatus: 'error', saveMessage: theErrorText, saveSnackbarDuration: null });
    });
  }

  disableTransfer(maxTransferAmount) {

 
    if (this.state.transferAmount < 1)
      return true;
   
    if (this.state.transferAmount > maxTransferAmount)
      return true;
    
    return false;
  }


  render() {
    const classes = this.props.classes;
    const { isAuthenticatedReseller } = this.props.auth;
    let autoTopupAmountString = getCurrencyString(this.props.autoTopupAmount);
    let autoTopupThresholdString = getCurrencyString(this.props.autoTopupThreshold);
      
    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={WalletBalanceWidgetsQuery}
        variables={{walletId: this.props.walletId, accountId: this.props.accountId, forceFetch: this.state.forceFetch}}
        render={({error, props})=> {

          if (error) {
            return <div>{error.message}</div>
          } else if (props) {
            // For balance widget
            let bal = props.getwalletledgersummary.assetAndFullArBalance;
            let balance = Number(bal).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

            // For available balance widget
            let ab = props.getwalletledgersummary.assetArAuthBalance;

            let availableBalance = Number(ab).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});
            abEnvironment.setUserAttribute("a_availableBalance", availableBalance);

            // Float widget
            let floatLimit = this.props.floatLimit;

            // Derive the float balance from equity balance and outstanding auth.
            let floatBalance = props.getwalletledgersummary.floatBalanceForAuth;
            if (floatBalance < 0) {
              floatBalance = 0;
            }

            // Default overdraft verbiage
            let floatLimitTitle1 = "Overdraft (";
            let floatLimitTitle2 = "% of limit)";
            let floatLimitTt =  "We may provide an overdraft float for purchasing subscriptions.  This plus your available balance is the amount available for subscription expenses."

            // Dialog titles.
            let floatCreateTitle = "Request overdraft";
            let floatIncreaseTitle = "Request an overdraft increase";
            let floatCreateMessage = "We decline charges that exceed your balance in AppBind.  Optionally, we provide an overdraft float that will allow charges to exceed your balance.  If you want to request an overdraft float, please indicate the size of the overdraft float you would like, and your reason for requesting an overdraft float.";
            let floatIncreaseMessage = "We're providing you with an overdraft float that allows charges to exceed your available balance.  To request an overdraft float increase, please indicate the size of the overdraft float you would like, and and your reason for requesting an increase.";

            //
            // There are 3 use cases we are supporting:
            // 1. Reseller looking at own account (on My funding)
            // 2. Customer looking at own account (on My funding)
            // 3. Reseller looking at customer account
            //
            // Depending on which case it is, there will be different verbiage in the various chicklets
            //

            // 1. Reseller looking at own account (default)
            let balanceTitle = "Wallet balance";
            let balanceTt = "The balance of your expense wallet.  This may include funds that haven't settled yet.";

            let availableBalanceTitle = "Wallet available balance";
            let availableBalanceTt = "Your available balance is the amount of funds in your expense wallet that are available to pay for subscriptions.  This may be lower than your total balance, as a portion of incoming funds will not be available until they have settled.";

            if (abEnvironment.getUserAttribute("a_aType") === "4") {

              // 2. Customer looking at own account.  Override here if necessary.
              balanceTitle = "Wallet balance";
              balanceTt = "The balance of your expense wallet.  This may include funds that haven't settled yet.";
              availableBalanceTitle = "Wallet available balance";
              availableBalanceTt = "Your available balance is the amount of funds in your expense wallet that are available to pay for subscriptions.  This may be lower than your total balance, as a portion of incoming funds will not be available until they have settled.";

            } else if (this.props.accountId !== abEnvironment.getUserAttribute("a_id")) {

              // 3. Reseller looking at customer account
              balanceTitle = "Wallet balance";
              balanceTt = "The balance of your customer's expense wallet.  This may include funds that haven't settled yet.";
              availableBalanceTitle = "Wallet available balance";
              availableBalanceTt = "Your customer's available balance is the amount of funds in their expense wallet that are available to pay for subscriptions.  This may be lower than the total balance, as a portion of incoming funds will not be available until they have settled.";

              floatLimitTt = "We may provide your customer with an overdraft float for purchasing subscriptions.  This, plus your customer's available balance, is the amount available for subscription expenses.  Each individual charge typically clears within a few days, but if you provision a large number of subscriptions, your customer may need a higher limit."
              floatCreateMessage = "We decline charges that exceed your customer's available balance in AppBind.  Optionally, we provide an overdraft float that will allow charges to exceed your customer's available balance.  If you want to request an overdraft float for your customer, please indicate the size of the overdraft float you would like, and your reason for requesting an overdraft float.";
              floatIncreaseMessage = "We're providing your customer with an overdraft float that allows charges to exceed your customer's available balance.  To request an overdraft float increase for your customer, please indicate the size of the overdraft float you would like, and and your reason for requesting an increase.";

            }

            let theDialogTitle = floatCreateTitle;
            let theDialogMessage = floatCreateMessage;
            if (this.props.floatLimit > 0) {
              theDialogTitle = floatIncreaseTitle;
              theDialogMessage = floatIncreaseMessage;
            }

            let walletId = null;
            if (props.allWalletTs.totalCount === 1) {
              walletId = props.allWalletTs.nodes[0].walletId;
            } else {
              //TODO more here in multi-wallet scenario
            }

            let abTransfer = Number(props.getwalletledgersummary.assetArAuthBalance) - Number(this.props.autoTopupThreshold);
            let availableBalanceTransfer = Number(abTransfer).toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

            if (this.state.executingTransfer) {
              return (<LoadingSpinner />);
            } else {
              return (
                <React.Fragment>
                  <Grid container spacing={3} justifyContent="flex-start" className={classes.widgetContainer}>
                    <Grid item>
                      <TextWidget
                        narrow={this.props.narrow}
                        value={balance}
                        description={balanceTitle}
                        tooltip={balanceTt}
                      />
                    </Grid>

                    <Grid item>
                      {/*<TextWidget
                        narrow={this.props.narrow}
                        value={availableBalance}
                        description={availableBalanceTitle}
                        tooltip={availableBalanceTt}
                      />*/}

                      <AvailableBalanceTextWidget
                        narrow={this.props.narrow}
                        value={availableBalance}
                        description={availableBalanceTitle}
                        tooltip={availableBalanceTt}
                        pendingReceipts={props.getunreceivedchargedetail.nodes}
                        accountPercentages={props.accountTByAccountId}
                      />
                    </Grid>
                    <Grid item>
                      <LinearProgressWidget
                        narrow={this.props.narrow}
                        nominator={floatBalance}
                        denominator={floatLimit}
                        descriptionStart={floatLimitTitle1}
                        descriptionEnd={floatLimitTitle2}
                        tooltip={floatLimitTt}
                      />
                    </Grid>
                    <Grid item>
                      <Grid container spacing={3} justifyContent="flex-start" className={classes.widgetContainer}>
                        {this.props.showLimitRequest &&
                          <Grid item  xs={12}>
                            <Button color="primary"  variant="outlined" size="small" 
                                    onClick={() => {this.showLimitRequestDialog()}}>
                              Request higher limits
                            </Button>
                          </Grid>
                        }
                                        
                        { isAuthenticatedReseller() && (Number(ab) > 0)  &&
                          <Grid item xs={12}>
                            <Tooltip title={"If your expense wallet balance is larger than you need it to be, you can transfer funds to your earnings balance.  If you automatically add funds to your expense wallet, you won't be able to transfer out an amount that would cause automated funding."}>
                              <Button color="primary"  variant="outlined" size="small" onClick={() => {this.showTransferDialog()}}>
                                Send to earnings balance
                              </Button>
                            </Tooltip>
                            </Grid>
                          }
                      </Grid>
                    </Grid>

                    {this.props.showEditLink &&
                    <Grid item xs={12}>
                      {(this.props.payFromBalance == null || this.props.payFromBalance === 0) &&
                        <Typography variant="body2" style={{ lineHeight: "2" }}>
                          <span>These subscriptions will be automatically charged to your funding sources as set in</span>&nbsp;&nbsp;
                          <Button color="primary" 
                            size="small" 
                            variant="outlined" 
                            className={classes.instructButton}
                            onClick={() => this.goTo('fundingsources', 1)}
                          >
                            <CreditCard className={classes.buttonicon}/> My Funding
                          </Button> 
                        </Typography>
                      }
  
                      {this.props.payFromBalance != null && this.props.payFromBalance !== 0 &&
                        <Typography variant="body2" style={{ lineHeight: "2" }}>
                          <span>Subscriptions will be charged to your wallet available balance of</span>&nbsp;
                          <span style={AppBindStyles.boldLabel}>{availableBalance}</span>
                          {this.props.autoTopupAmount && this.props.autoTopupThreshold &&
                            <React.Fragment>
                              .&nbsp;&nbsp;
                              <span>Your balance will be topped up to</span>&nbsp;
                              <span style={AppBindStyles.boldLabel}>{autoTopupAmountString}</span>&nbsp;
                              <span>when it falls below</span>&nbsp;
                              <span style={AppBindStyles.boldLabel}>{autoTopupThresholdString}</span>
                            </React.Fragment>
                          }

                          &nbsp;<span>as set in</span>&nbsp;&nbsp;
                          <Button color="primary" 
                            size="small" 
                            variant="outlined" 
                            className={classes.instructButton}
                            onClick={() => this.goTo('fundingsources', 1)}
                          >
                            <CreditCard className={classes.buttonicon}/> My Funding
                          </Button>
                        </Typography>
                      }
                    </Grid>
                    }
                  </Grid>
                  <LimitRequestDialog
                    open={this.state.showLimitRequestDialog}
                    onClose={() => { this.setState({showLimitRequestDialog:false});}}
                    onConfirm={() => { this.sendLimitIncreaseRequest(this.props.accountId)}}
                    onCancel={() => { this.setState({showLimitRequestDialog:false});}}
                    reason={this.state.limitRequestReason}
                    title={theDialogTitle}
                    cancelBtn="Cancel"
                    okBtn="Send Request"
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body1">{theDialogMessage}</Typography>
                      </Grid>
                      <Grid item>
                        <CurrencyTextField 
                          id={"balance-limit"}
                          label={"Overdraft limit"}
                          value={this.state.floatLimit}
                          onChange={ (e) => this.setState({ floatLimit: e.target.value }) }
                          showErrMsg={true}
                          allowNegative={false}
                          minValue={0}
                          minEnterValue={0}
                          textAlign="right"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          id="reason-text" label="Reason for increase" 
                          value={this.state.limitRequestReason}
                          fullWidth
                          multiline
                          onChange={(e) => this.setState({ limitRequestReason: e.target.value })}
                          inputProps={{ maxLength: 2048 }} />
                      </Grid>
                    </Grid>
                  </LimitRequestDialog>
  
                  <InitiateTransferDialog
                    open={this.state.showTransferDialog}
                    onClose={() => { this.setState({showTransferDialog:false}); }}
                    onConfirm={() => { this.initiateTransfer(this.props.accountId, walletId) }}
                    onCancel={() => { this.setState({showTransferDialog:false}); }}
                    amount={this.state.transferAmount}
                    title={'Transfer to earnings balance'}
                    cancelBtn="Cancel"
                    editBtn={false}
                    okBtn={"Transfer"}
                    okDisabled={this.disableTransfer(Number(abTransfer))}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body1">You have up to {availableBalanceTransfer} to transfer to  your earnings balance</Typography>                         
                      </Grid>

                      <Grid item xs={12}>
                        <CurrencyTextField 
                          id="transfer-amount" label="Transfer amount"
                          value={this.state.transferAmount}
                          onChange={ (e) => this.setState({ transferAmount: e.target.value }) }
                          showErrMsg={true}
                          allowNegative={false}
                          minValue={1}
                          minEnterValue={0}
                          maxValue={Number(ab)}
                          maxEnterValue={Number(ab)}
                          textAlign="right"
                        />

                      </Grid>
                    </Grid>
                  </InitiateTransferDialog>

                </React.Fragment>
              );
            }
          }            
        }}
      />
    );
  }
}

export default withStyles(styleSheet)(WalletBalanceWidgets);